"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidConnectFlow/steps/PlaidDenseWallStep";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidDenseWallStepProps {
  onContinue: () => void;
  onClose?: () => void;
  /**
   * 关闭按钮额外上报的**业务事件名**（点击 label `plaidconnectflow_densewall_close` 照旧，两者不冲突）。
   *
   * 为什么做成可选 prop 而不是写死在组件里：本组件在默认支的 Plaid 流里也用，
   * 写死会让默认支凭空多出一条 `onb_connect_skip`，统计口径跳变。只有 onboarding
   * 广告支的 `connect` 屏传它。
   */
  closeOrEvent?: string;
  /**
   * 与 {@link closeOrEvent} 同元素的定位参数（规范 §1.6：多处复用的落点必须带得出「是哪一处」）。
   * 传了 `closeOrEvent` 就必须传它。
   */
  closeOrSource?: string;
}

const ROW_1_BUBBLES = [
  {
    name: "Schwab",
    src: "/assets/images/plaid/dense-wall/bubble-r1-1-schwab.webp",
  },
  {
    name: "Vanguard",
    src: "/assets/images/plaid/dense-wall/bubble-r1-2-vanguard.webp",
  },
  {
    name: "Robinhood",
    src: "/assets/images/plaid/dense-wall/bubble-r1-3-robinhood.webp",
  },
  {
    name: "E*TRADE",
    src: "/assets/images/plaid/dense-wall/bubble-r1-4-etrade.webp",
  },
  {
    name: "Coinbase",
    src: "/assets/images/plaid/dense-wall/bubble-r1-5-coinbase.webp",
  },
  {
    name: "RedLogo",
    src: "/assets/images/plaid/dense-wall/bubble-r1-6-redlogo.webp",
  },
  {
    name: "Fidelity",
    src: "/assets/images/plaid/dense-wall/bubble-r1-7-fidelity.webp",
  },
];

const ROW_2_BUBBLES = [
  {
    name: "Wells Fargo",
    src: "/assets/images/plaid/dense-wall/bubble-r2-1-wf.webp",
  },
  {
    name: "Bank of America",
    src: "/assets/images/plaid/dense-wall/bubble-r2-2-bofa.webp",
  },
  {
    name: "American Express",
    src: "/assets/images/plaid/dense-wall/bubble-r2-3-amex.webp",
  },
  {
    name: "Schwab",
    src: "/assets/images/plaid/dense-wall/bubble-r2-4-schwab.webp",
  },
  {
    name: "Chase",
    src: "/assets/images/plaid/dense-wall/bubble-r2-5-chase.webp",
  },
  {
    name: "Capital One",
    src: "/assets/images/plaid/dense-wall/bubble-r2-6-capitalone.webp",
  },
];

export const PlaidDenseWallStep: React.FC<PlaidDenseWallStepProps> = ({
  onContinue,
  onClose,
  closeOrEvent,
  closeOrSource,
}) => {
  return (
    <div className="relative flex-1 flex flex-col justify-between overflow-y-auto overflow-x-hidden bg-[#0a231c] text-white">
      {/* Background Graphic */}
      <div
        className="absolute inset-0 bg-cover bg-center pointer-events-none opacity-90 z-0"
        style={{
          backgroundImage: `url('/assets/images/plaid/dense-wall/bg-dense-wall.webp')`,
        }}
      />

      {/* Top Header */}
      <div className="relative z-10 flex items-center justify-between w-full px-4 py-3 md:px-6 md:py-4 shrink-0 min-h-[48px]">
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            data-openreplay-label="plaidconnectflow_densewall_close"
            data-or-event={closeOrEvent}
            data-or-param-source={closeOrEvent ? closeOrSource : undefined}
            className="size-10 rounded-full bg-background_solid_primary shadow-[0px_2px_10px_rgba(0,0,0,0.08)] flex items-center justify-center text-content_primary hover:opacity-90 active:scale-95 transition-all cursor-pointer"
            aria-label={I18n.t("Close")}
          >
            <Icon name="close" size={20} />
          </button>
        ) : (
          <div className="size-10" />
        )}

        {/* Center Ed Logo */}
        <div className="flex items-center justify-center h-6">
          <img
            src="/assets/images/plaid/dense-wall/ed-logo-white.svg"
            alt="Ed"
            className="h-5 w-auto object-contain"
          />
        </div>

        <div className="size-10" />
      </div>

      {/* Main Content Area */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-between max-w-[361px] mx-auto w-full px-4 py-2 gap-6">
        {/* Intro Copy */}
        <div className="flex flex-col gap-3 text-center w-full mt-2">
          <h1 className="font-['Lexend'] text-[32px] font-normal leading-[38px] tracking-[-1.28px] text-white">
            {I18n.t("Connect your accounts")}
          </h1>
          <p className="text-base text-white/90 leading-6 font-normal">
            {I18n.t(
              "Ed'll read it to find money you're leaving on the table, watch it against your goal, and flag anything worth acting on. Read-only, always.",
            )}
          </p>
        </div>

        {/* Trust Wreath Badges */}
        <div className="flex items-center justify-center gap-4 w-full">
          {/* Badge 1: Bank-Level Encryption */}
          <div className="flex items-center justify-center relative shrink-0">
            <img
              src="/assets/images/plaid/dense-wall/wreath-left.svg"
              alt=""
              className="h-[60px] w-auto opacity-70 shrink-0 select-none pointer-events-none"
            />
            <div className="flex flex-col items-center text-center px-1 -mx-2.5 z-10 w-[96px]">
              <div className="size-8 flex items-center justify-center mb-1">
                <img
                  src="/assets/images/plaid/dense-wall/icon-check-shield.svg"
                  alt="Bank-Level Encryption"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <div className="text-xs font-medium text-white leading-4 text-center">
                <p className="leading-4 mb-0">{I18n.t("Bank-Level")}</p>
                <p className="leading-4">{I18n.t("Encryption")}</p>
              </div>
            </div>
            <img
              src="/assets/images/plaid/dense-wall/wreath-right.svg"
              alt=""
              className="h-[60px] w-auto opacity-70 shrink-0 select-none pointer-events-none [transform:rotateY(180deg)]"
            />
          </div>

          {/* Badge 2: Read-only, never shared */}
          <div className="flex items-center justify-center relative shrink-0">
            <img
              src="/assets/images/plaid/dense-wall/wreath-left.svg"
              alt=""
              className="h-[60px] w-auto opacity-70 shrink-0 select-none pointer-events-none"
            />
            <div className="flex flex-col items-center text-center px-1 -mx-2.5 z-10 w-[96px]">
              <div className="size-8 flex items-center justify-center mb-1">
                <img
                  src="/assets/images/plaid/dense-wall/icon-lock-shield.svg"
                  alt="Read-only, never shared"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <div className="text-xs font-medium text-white leading-4 text-center">
                <p className="leading-4 mb-0">{I18n.t("Read-only,")}</p>
                <p className="leading-4">{I18n.t("never shared")}</p>
              </div>
            </div>
            <img
              src="/assets/images/plaid/dense-wall/wreath-right.svg"
              alt=""
              className="h-[60px] w-auto opacity-70 shrink-0 select-none pointer-events-none [transform:rotateY(180deg)]"
            />
          </div>
        </div>

        {/* Logo Marquee Section */}
        <div className="flex flex-col gap-3 items-center w-full overflow-hidden">
          {/* Row 1 (Moving Left) */}
          <div className="w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
            <div className="flex gap-2 items-center w-max animate-[marquee-left_25s_linear_infinite]">
              {[...ROW_1_BUBBLES, ...ROW_1_BUBBLES, ...ROW_1_BUBBLES].map(
                (item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="size-11 rounded-full overflow-hidden border border-white/15 bg-black/20 shrink-0"
                  >
                    <img
                      src={item.src}
                      alt={item.name}
                      className="w-full h-full object-cover rounded-full pointer-events-none"
                    />
                  </div>
                ),
              )}
            </div>
          </div>

          {/* Row 2 (Moving Right) */}
          <div className="w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
            <div className="flex gap-2 items-center w-max animate-[marquee-right_25s_linear_infinite]">
              {[...ROW_2_BUBBLES, ...ROW_2_BUBBLES, ...ROW_2_BUBBLES].map(
                (item, idx) => (
                  <div
                    key={`${item.name}-${idx}`}
                    className="size-11 rounded-full overflow-hidden border border-white/15 bg-black/20 shrink-0"
                  >
                    <img
                      src={item.src}
                      alt={item.name}
                      className="w-full h-full object-cover rounded-full pointer-events-none"
                    />
                  </div>
                ),
              )}
            </div>
          </div>

          <p className="text-xs text-white/80 text-center mt-1">
            {I18n.t("Over 12,000 institutions supported")}
          </p>
        </div>

        {/* Bottom CTA Button */}
        <div className="w-full pb-4 pt-2">
          <button
            type="button"
            onClick={onContinue}
            data-openreplay-label="plaidconnectflow_densewall_continue"
            className="w-full h-11 rounded-lg bg-background_solid_primary text-content_primary font-medium text-base hover:bg-background_solid_primary/95 active:scale-[0.99] transition-all cursor-pointer flex items-center justify-center shadow-lg"
          >
            {I18n.t("Continue")}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes marquee-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        @keyframes marquee-right {
          0% {
            transform: translateX(-33.333%);
          }
          100% {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};
